import {
  ClickAwayListener,
  Grow,
  IconButton,
  Paper,
  Popper as MUIPopper,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { SeriesSettingsIcon } from '@/shared/ui';

const Popper = styled(MUIPopper)(({ theme }) => ({
  zIndex: theme.zIndex.modal,
}));

const NESTED_POPUP_SELECTOR = '.MuiPopover-root, .MuiAutocomplete-popper';

interface Props {
  disabled?: boolean;
  disabledReason?: string;
  ariaLabel: string;
  children: ReactNode;
}

export function AxisSeriesMenuButton({
  disabled,
  disabledReason,
  ariaLabel,
  children,
}: Props) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    const target = event.target as Element | null;
    const insideNestedPopup =
      !!target?.closest?.(NESTED_POPUP_SELECTOR) ||
      !!document.querySelector(NESTED_POPUP_SELECTOR);

    if (insideNestedPopup) {
      return;
    }

    setOpen(false);
  };

  const button = (
    <IconButton
      ref={anchorRef}
      aria-label={ariaLabel}
      disabled={disabled}
      sx={{
        width: 40,
        height: 40,
        border: '1px solid',
        borderColor: open ? 'primary.main' : 'rgba(0, 0, 0, 0.23)',
        borderRadius: 2,
        color: open ? 'primary.main' : '#343844',
        backgroundColor: open ? '#EBEFFF' : 'transparent',
      }}
      onClick={() => setOpen((wasOpen) => !wasOpen)}
    >
      <SeriesSettingsIcon sx={{ fontSize: 20 }} />
    </IconButton>
  );

  return (
    <>
      {disabled && disabledReason ? (
        <Tooltip arrow title={disabledReason}>
          <span>{button}</span>
        </Tooltip>
      ) : (
        button
      )}
      {open && (
        <Popper
          open
          anchorEl={anchorRef.current}
          placement="bottom-end"
          transition
          modifiers={[
            { name: 'preventOverflow', options: { altAxis: true, padding: 8 } },
          ]}
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps} style={{ transformOrigin: 'right top' }}>
              <Paper
                sx={{
                  mt: 0.5,
                  maxHeight: 'calc(100vh - 16px)',
                  overflowY: 'auto',
                  overscrollBehavior: 'contain',
                }}
                elevation={3}
              >
                <ClickAwayListener onClickAway={handleClickAway}>
                  <div>{children}</div>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      )}
    </>
  );
}
